import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Input from '@components/Input';
import Slider from '@components/Slider';
import Tip from '@components/Tip';
import useInput from '@hooks/useInput';
import { C_LAYER_DEFAULT } from '@modules/componentLayer';
import {
  $editingComponentLayerApi,
  onLayerAnimationPreview,
} from '@stores/editingComponentLayerStores';
import type {
  ComponentLayerAnimation,
  ComponentLayerAnimationCategory,
} from '@types';
import limitNum from '@utils/limitNum';
import { roundNum } from '@utils/roundNum';
import { useMemo, useState } from 'react';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  animationEnterList,
  animationExitList,
  animationList,
} from './animationList';

interface Props {
  category: ComponentLayerAnimationCategory;
  animation: ComponentLayerAnimation;
}

type LoopType = 'infinite' | 'stop' | 'hide';
const loopItems: DropdownItem<LoopType>[] = [
  { id: 'infinite', name: '无限循环播放' },
  { id: 'stop', name: '播放固定次数后停止' },
  { id: 'hide', name: '播放固定次数后隐藏' },
];

const stayItems: DropdownItem<ComponentLayerAnimation['stayMode']>[] = [
  { id: 0, name: '始终停留' },
  { id: 1, name: '停留一定时长后隐藏' },
];

export function LayerAnimations({ animation, category }: Props) {
  const [lootType, setLoopType] = useState<LoopType>(() => {
    if (category !== 'animation') return 'infinite';
    if (animation.loopCount !== 0) {
      if (animation.playEndAction === 1) return 'hide';
      return 'stop';
    }
    return 'infinite';
  });
  const [countValue, setCountValue] = useState<string>(
    String(animation?.loopCount ?? 1)
  );
  const stayInput = useInput<HTMLInputElement>(
    String(animation.stayDuration ?? 5)
  );

  const list = useMemo(() => {
    if (category === 'enterAnimation') return animationEnterList;
    if (category === 'exitAnimation') return animationExitList;
    return animationList;
  }, [category]);

  const updateAnimation = <K extends keyof ComponentLayerAnimation>(
    key: K,
    value: ComponentLayerAnimation[K]
  ) => {
    $editingComponentLayerApi.update((draft) => {
      const data = draft[category] ?? C_LAYER_DEFAULT[category];
      data[key] = value;
      if (key === 'type') {
        if (value === 0) {
          // 改为无动画时, 重置动画参数
          const presetDuration = list.find(
            (item) => item.id === value
          )?.duration;
          data.duration = presetDuration ?? C_LAYER_DEFAULT.animation.duration;
          data.delay = C_LAYER_DEFAULT.animation.delay;
          data.loopCount = C_LAYER_DEFAULT.animation.loopCount;
          data.playEndAction = C_LAYER_DEFAULT.animation.playEndAction;
        } else if (
          // 进入/退出动画类型切换时, 重置持续时间
          category === 'enterAnimation' ||
          category === 'exitAnimation'
        ) {
          const presetDuration = list.find(
            (item) => item.id === value
          )?.duration;
          data.duration = presetDuration ?? C_LAYER_DEFAULT.animation.duration;
        }
      }
      draft[category] = data;
    });
  };

  const handleCountInputDone = (val: string) => {
    const count = roundNum(
      0,
      limitNum({ min: 1, max: 99, value: Number(val) })
    );
    setCountValue(String(count));
    updateAnimation('loopCount', count);
  };

  return (
    <>
      <Collapse title="动画类型">
        <Dropdown
          items={list}
          selected={animation.type}
          onChange={(value) => updateAnimation('type', value)}
        />
      </Collapse>
      {animation.type !== 0 && (
        <Collapse title="动画效果">
          <Slider
            value={animation.duration}
            min={0.1}
            max={10}
            onChange={(val) =>
              updateAnimation(
                'duration',
                limitNum({ min: 0.1, max: 10, value: val })
              )
            }
          >
            <Slider.Header
              title={
                <div className="flex items-center">
                  动画时长
                  <Tip className="ml-5">单次动画的时长</Tip>
                </div>
              }
            />
            <Slider.Bar step={0.1} />
            <Slider.Control step={0.1} />
          </Slider>
          <Slider
            className="mt-20"
            value={animation.delay}
            min={0}
            max={10}
            onChange={(val) => updateAnimation('delay', val)}
          >
            <Slider.Header
              title={
                <div className="flex items-center">
                  延迟时长
                  <Tip className="ml-5">延迟后再动画</Tip>
                </div>
              }
            />
            <Slider.Bar step={0.1} />
            <Slider.Control step={0.1} />
          </Slider>
          {category === 'animation' && (
            <>
              <div className="mt-20 mb-10">播放方式</div>
              <Dropdown
                items={loopItems}
                selected={lootType}
                onChange={(value) => {
                  setLoopType(value);
                  if (value === 'stop') {
                    if (lootType === 'infinite') {
                      updateAnimation('loopCount', 1);
                      setCountValue('1');
                    }
                    updateAnimation('playEndAction', 0);
                  } else if (value === 'hide') {
                    if (lootType === 'infinite') {
                      updateAnimation('loopCount', 1);
                      setCountValue('1');
                    }
                    updateAnimation('playEndAction', 1);
                  } else {
                    updateAnimation('loopCount', 0);
                    updateAnimation('playEndAction', 0);
                  }
                }}
              />
            </>
          )}
          {(lootType === 'stop' || lootType === 'hide') && (
            <>
              <div className="mt-20 mb-10">播放次数</div>
              <div className="flex items-center">
                <Input
                  value={countValue}
                  type="number"
                  scheme="dark"
                  max={99}
                  className="h-40 grow"
                  onChange={(e) => {
                    setCountValue(e.target.value);
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      handleCountInputDone(
                        (e.target as HTMLInputElement).value.trim()
                      );
                    } else if (e.key === 'Escape') {
                      setCountValue(String(animation.loopCount));
                    }
                  }}
                  onBlur={(e) => {
                    handleCountInputDone(e.target.value.trim());
                  }}
                />
                <div className="ml-10 text-black-gray">次</div>
              </div>
            </>
          )}
          <Button
            className="mt-20 w-full"
            onClick={() => {
              onLayerAnimationPreview();
            }}
          >
            预览动画
          </Button>
        </Collapse>
      )}
      {animation.type === 0 && category === 'animation' && (
        <Collapse title="动画效果">
          <Dropdown
            items={stayItems}
            selected={animation.stayMode ?? 0}
            onChange={(value) => {
              updateAnimation('stayMode', value);
            }}
          />
          {animation.stayMode === 1 && (
            <>
              <div className="mt-20">停留时长</div>
              <div className="flex items-center mt-10">
                <Input
                  scheme="dark"
                  className="h-40 grow"
                  type="number"
                  {...stayInput.bind}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      stayInput.ref.current?.blur();
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (!isNumeric(val)) {
                      stayInput.setValue(String(animation.stayDuration ?? 5));
                      return;
                    }
                    const duration = roundNum(
                      0,
                      limitNum({ min: 1, max: 99, value: Number(val) })
                    );
                    stayInput.setValue(String(duration));
                    updateAnimation('stayDuration', duration);
                  }}
                />
                <div className="ml-10 text-black-gray">秒</div>
              </div>
            </>
          )}
        </Collapse>
      )}
    </>
  );
}

export default LayerAnimations;
